/**
 * A minimal positioned **context menu** with one or more actions, shared by
 * the surfaces that need a lightweight popover: the in-world sprite **long-press of left-click**
 * (which offers "Ignore &lt;name&gt;" and, on a player, "Roll the dice" to start a dice
 * wager, ADR-0154, and "Duel &lt;name&gt;" to challenge them, ADR-0160 - the trigger moved
 * from shift+right-click to shift+left-click in ADR-0161, then to a long-press of left-click
 * in ADR-0169, so right-click stays movement), the chat-journal line right-click
 * ("Ignore &lt;name&gt;"), and the dice-and-cup long-press ("Roll the dice"). The app has no general context-menu
 * primitive and the brief calls for a lightweight one, so this is a single positioned
 * element with a short vertical stack of buttons - not a menu system.
 *
 * Discipline matches `chat.ts`/`settings.ts`: each row is built with
 * `createElement`/`textContent` only (never `innerHTML`), so a hostile display name is
 * shown as text, never parsed as markup. Only one menu exists at a time; opening a new
 * one (or a click outside / Escape / scroll / resize) dismisses the previous.
 */

/** One action row in a context menu: its label and what committing it does. */
export interface ContextMenuItem {
  /** The button label, shown verbatim as text (never markup). */
  label: string;
  /** Called when the player commits this row; the menu then closes. */
  onSelect: () => void;
}

/** Options for {@link openContextMenu}; `doc` is injected by tests. */
export interface ContextMenuOptions {
  /** The action rows, top to bottom. */
  items: ContextMenuItem[];
  /** Viewport x (clientX) to anchor the menu's left edge at. */
  x: number;
  /** Viewport y (clientY) to anchor the menu's top edge at. */
  y: number;
  /** The document to mount in (defaults to the live `document`); injected by tests. */
  doc?: Document;
  /**
   * The element id to give the menu (defaults to {@link MENU_ID}). Kept so the
   * long-standing ignore popover keeps its stable `ignore-menu` id (and its test) while
   * other callers reuse the same primitive.
   */
  id?: string;
}

/** Options for {@link openIgnoreMenu} - the single-action ignore popover. */
export interface IgnoreMenuOptions {
  /** The display name the action will ignore (shown verbatim in the button label). */
  name: string;
  /** Viewport x (clientX) to anchor the menu's left edge at. */
  x: number;
  /** Viewport y (clientY) to anchor the menu's top edge at. */
  y: number;
  /** Called with `name` when the player commits the Ignore action. */
  onIgnore: (name: string) => void;
  /** The document to mount in (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The default id of the singleton menu element, so opening/closing can find it. */
const MENU_ID = 'ignore-menu';

/**
 * The active menu's full teardown (remove the element *and* detach its document/window
 * listeners), or `null` when no menu is open. Held at module scope so
 * {@link closeContextMenu} tears the previous menu down completely - element and every
 * listener - however it is dismissed, including when a new open replaces an existing menu
 * without an intervening outside-pointerdown/scroll/Escape.
 */
let activeClose: (() => void) | null = null;

/** Dismiss any open context menu (and its outside-dismiss listeners). */
export function closeContextMenu(doc: Document = document): void {
  if (activeClose) {
    activeClose();
    return;
  }
  // No tracked menu (e.g. one left by a prior call); still remove any stray element.
  doc.getElementById(MENU_ID)?.remove();
}

/**
 * Open a positioned context menu at (`x`, `y`) with the given action rows, replacing any
 * menu already open. Committing a row runs its `onSelect` and closes; a click/right-click
 * anywhere else, Escape, a scroll, or a resize dismisses it without acting.
 */
export function openContextMenu(opts: ContextMenuOptions): void {
  const doc = opts.doc ?? document;
  const id = opts.id ?? MENU_ID;
  closeContextMenu(doc);

  const menu = doc.createElement('div');
  menu.id = id;
  menu.className = 'context-menu';
  menu.style.left = `${opts.x}px`;
  menu.style.top = `${opts.y}px`;

  const close = (): void => {
    menu.remove();
    doc.removeEventListener('pointerdown', onOutside, true);
    doc.removeEventListener('keydown', onKey, true);
    (doc.defaultView ?? window).removeEventListener('scroll', close, true);
    (doc.defaultView ?? window).removeEventListener('resize', close);
    // Clear the module ref only if we are still the active menu: a later openContextMenu
    // may have already installed its own close() before an old listener fires.
    if (activeClose === close) activeClose = null;
  };

  for (const spec of opts.items) {
    const item = doc.createElement('button');
    item.type = 'button';
    item.className = 'context-menu-item';
    // textContent, never innerHTML: a hostile name is shown as text, never parsed.
    item.textContent = spec.label;
    item.addEventListener('click', () => {
      spec.onSelect();
      close();
    });
    menu.appendChild(item);
  }

  // A pointerdown outside the menu (either button) dismisses it. Capture phase so it
  // beats the canvas's own right-click-to-walk handler on the way down.
  const onOutside = (e: Event): void => {
    if (!menu.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };

  doc.body.appendChild(menu);
  doc.addEventListener('pointerdown', onOutside, true);
  doc.addEventListener('keydown', onKey, true);
  (doc.defaultView ?? window).addEventListener('scroll', close, true);
  (doc.defaultView ?? window).addEventListener('resize', close);
  activeClose = close;
}

/** Dismiss any open ignore/context menu (kept as the historical name for callers/tests). */
export function closeIgnoreMenu(doc: Document = document): void {
  closeContextMenu(doc);
}

/**
 * Open the single-action Ignore popover at (`x`, `y`), replacing any menu already open.
 * A thin wrapper over {@link openContextMenu} preserving the stable `ignore-menu` id.
 */
export function openIgnoreMenu(opts: IgnoreMenuOptions): void {
  openContextMenu({
    id: MENU_ID,
    x: opts.x,
    y: opts.y,
    doc: opts.doc,
    items: [{ label: `Ignore ${opts.name}`, onSelect: () => opts.onIgnore(opts.name) }],
  });
}
